import { useEffect, useState } from "react";

import LaneEditor, { callsPerDay } from "../components/LaneEditor";
import { ago } from "../components/MatchCard";
import { api } from "../lib/api";
import type { Budget, Lane, Watch } from "../lib/types";

/** Lanes are the only thing that costs API calls, so this screen leads with the budget. */
export default function Lanes() {
  const [lanes, setLanes] = useState<Lane[]>([]);
  const [watches, setWatches] = useState<Watch[]>([]);
  const [budget, setBudget] = useState<Budget | null>(null);
  const [editing, setEditing] = useState<Lane | null | "new">(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = async () => {
    try {
      const [l, w, b] = await Promise.all([api.lanes(), api.watches(), api.budget()]);
      setLanes(l);
      setWatches(w);
      setBudget(b);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    void load();
  }, []);

  async function patch(lane: Lane, body: Partial<Lane>) {
    try {
      const updated = await api.patchLane(lane.id, body);
      setLanes((prev) => prev.map((l) => (l.id === lane.id ? updated : l)));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function reset(lane: Lane) {
    if (
      !confirm(
        `Re-run "${lane.name}" listings from the last 30 days through its current rules?

` +
          "Stage 0 is re-applied (so listings an exclude term killed come back) and every " +
          "verdict for this lane is cleared, so its items leave the feed until the model " +
          "confirms them again. Nothing pushes twice, and no listing is deleted.",
      )
    )
      return;
    try {
      const result = await api.resetLane(lane.id, {
        rerun_stage0: true,
        clear_verdicts: true,
        rejects_only: false,
        clear_watermark: false,
        days: 30,
      });
      setNotice(result.note);
      window.setTimeout(() => setNotice(null), 20000);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function remove(lane: Lane) {
    const scoped = watches.filter((w) => w.lane_id === lane.id);
    const warning = scoped.length
      ? `\n\n${scoped.length} watch(es) are scoped to it and will become all-lanes watches: ${scoped
          .map((w) => w.name)
          .join(", ")}`
      : "";
    if (
      !confirm(
        `Delete lane "${lane.name}"? Its listings and verdicts are kept, so the reject log ` +
          `stays readable.${warning}`,
      )
    )
      return;
    try {
      await api.deleteLane(lane.id);
      if (editing !== "new" && editing?.id === lane.id) setEditing(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  const projected = lanes.filter((l) => l.enabled).reduce((sum, l) => sum + callsPerDay(l), 0);
  const cap = budget?.cap ?? 5000;

  return (
    <div className="stack">
      {error && <div className="error">{error}</div>}
      {notice && (
        <div className="card" style={{ borderColor: "var(--accent)" }}>
          <span className="faint">{notice}</span>
        </div>
      )}

      <div className="row">
        <span className={`pill ${projected > cap * 0.9 ? "risk" : "pass"}`}>
          ~{projected.toLocaleString()} of {cap.toLocaleString()} calls/day across{" "}
          {lanes.filter((l) => l.enabled).length} enabled lane(s)
        </span>
        <div className="spacer" />
        {editing === null && <button onClick={() => setEditing("new")}>New lane</button>}
      </div>

      {editing !== null && (
        <LaneEditor
          lane={editing === "new" ? null : editing}
          onCancel={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}

      {lanes.map((lane) => {
        const listeners = watches.filter((w) => w.lane_id === lane.id || w.lane_id === null);
        return (
          <div className="card stack" key={lane.id}>
            <div className="row" style={{ justifyContent: "space-between" }}>
              <div>
                <strong>{lane.name}</strong>
                <div className="faint">
                  {lane.keywords ? `q="${lane.keywords}"` : "no keywords"} -{" "}
                  {lane.category_ids.length
                    ? `categories ${lane.category_ids.join(", ")}`
                    : "no category"}{" "}
                  - {lane.marketplace_id}
                  {lane.max_price !== null || lane.min_price !== null
                    ? ` - ${lane.min_price ?? 0}..${lane.max_price ?? "any"}`
                    : ""}
                </div>
              </div>
              <div className="row">
                <span className="pill">~{callsPerDay(lane)} calls/day</span>
                <span
                  className={`pill ${
                    !lane.enabled ? "warn" : listeners.length ? "pass" : "warn"
                  }`}
                >
                  {!lane.enabled
                    ? "paused"
                    : listeners.length
                      ? "polling"
                      : lane.pause_when_unwatched
                        ? "idle - unwatched"
                        : "polling (unwatched)"}
                </span>
              </div>
            </div>

            <div className="row">
              <span className="pill">
                {lane.poll_interval_day}s day / {lane.poll_interval_night}s night
              </span>
              <span className="pill">
                {lane.exclude_terms.length} exclude term(s)
              </span>
              <span className={`pill ${listeners.length ? "" : "warn"}`}>
                {listeners.length
                  ? `${listeners.length} watch(es) listening`
                  : lane.pause_when_unwatched
                    ? "no watch listening — not polling, so it costs nothing"
                    : "no watch listening — still polling, spending budget"}
              </span>
              {(lane.exclude_condition_ids?.length ?? 0) > 0 && (
                <span className="pill">
                  {lane.exclude_condition_ids!.length} condition(s) excluded
                </span>
              )}
              <div className="spacer" />
              <button onClick={() => setEditing(lane)}>Edit</button>
              <button onClick={() => void reset(lane)}>Re-rate</button>
              <button onClick={() => void patch(lane, { enabled: !lane.enabled })}>
                {lane.enabled ? "Pause" : "Enable"}
              </button>
              <button className="ghost danger" onClick={() => void remove(lane)}>
                Delete
              </button>
            </div>

            <div className="faint">
              last polled {ago(lane.last_polled_at)} - watermark{" "}
              {lane.last_seen_start_date
                ? new Date(lane.last_seen_start_date).toLocaleString()
                : "none"}
            </div>
          </div>
        );
      })}

      {!lanes.length && editing === null && (
        <div className="empty">
          No lanes configured. A lane is what you poll — broad and dumb; the narrowing happens in
          a watch, for free.
        </div>
      )}
    </div>
  );
}
